/**
 * Mock Budget Storage
 * 
 * This module provides a simple localStorage-based storage solution for mock budget data
 * in demo mode. This ensures that changes made in the Edit Sales/Resource Budget screens are
 * reflected in the respective tab views.
 * 
 * In production, this will be replaced with actual API calls to the backend.
 */

import type { CategoryBudgetRow, RoleBudget } from '@/types/budget.types';

const STORAGE_KEY_PREFIX = 'mock_sales_budget_';

export interface StoredSalesBudgetCategories {
  projectId: number;
  categories: CategoryBudgetRow[];
  lastUpdated: string;
}

/**
 * Save sales budget categories to localStorage
 */
export function saveMockSalesBudgetCategories(
  projectId: number,
  categories: CategoryBudgetRow[]
): void {
  const data: StoredSalesBudgetCategories = {
    projectId,
    categories,
    lastUpdated: new Date().toISOString(),
  };
  
  try {
    const key = `sales-budget-categories-${projectId}`;
    localStorage.setItem(key, JSON.stringify(categories));
    
    // Also save with the old key format for backward compatibility
    localStorage.setItem(
      `${STORAGE_KEY_PREFIX}${projectId}`,
      JSON.stringify(data)
    );
    
    // Dispatch custom event for same-tab updates
    window.dispatchEvent(new CustomEvent('localStorageChange', {
      detail: { key, value: categories }
    }));
    
    console.log('[MockBudgetStorage] Saved sales budget categories for project', projectId);
  } catch (error) {
    console.error('[MockBudgetStorage] Error saving to localStorage:', error);
  }
}

/**
 * Load sales budget categories from localStorage
 */
export function loadMockSalesBudgetCategories(
  projectId: number
): CategoryBudgetRow[] | null {
  try {
    const stored = localStorage.getItem(`${STORAGE_KEY_PREFIX}${projectId}`);
    if (!stored) {
      return null;
    }
    
    const data: StoredSalesBudgetCategories = JSON.parse(stored);
    console.log('[MockBudgetStorage] Loaded sales budget categories for project', projectId);
    return data.categories;
  } catch (error) {
    console.error('[MockBudgetStorage] Error loading from localStorage:', error);
    return null;
  }
}

/**
 * Clear sales budget categories from localStorage
 */
export function clearMockSalesBudgetCategories(projectId: number): void {
  try {
    localStorage.removeItem(`${STORAGE_KEY_PREFIX}${projectId}`);
    console.log('[MockBudgetStorage] Cleared sales budget categories for project', projectId);
  } catch (error) {
    console.error('[MockBudgetStorage] Error clearing localStorage:', error);
  }
}

/**
 * Clear all mock budget data from localStorage
 */
export function clearAllMockBudgetData(): void {
  try {
    const keys = Object.keys(localStorage);
    keys.forEach(key => {
      if (key.startsWith(STORAGE_KEY_PREFIX)) {
        localStorage.removeItem(key);
      }
    });
    console.log('[MockBudgetStorage] Cleared all mock budget data');
  } catch (error) {
    console.error('[MockBudgetStorage] Error clearing all data:', error);
  }
}

// ── Resource Budget mock storage (Phase 5 — Cost Centre mode) ─────────────
// Used to persist byRole data entered in the Resource Budget edit screen (demo mode)
// so the tab view and refetchBudget() return the saved data rather than the original mock.

const RESOURCE_BUDGET_STORAGE_KEY = (projectId: number) =>
  `mock_resource_budget_${projectId}`;

export interface StoredResourceBudget {
  projectId: number;
  byRole: RoleBudget[];
  lastUpdated: string;
}

/**
 * Save resource budget role data to localStorage (demo mode only).
 */
export function saveMockResourceBudgetRoles(
  projectId: number,
  byRole: RoleBudget[]
): void {
  try {
    const data: StoredResourceBudget = {
      projectId,
      byRole,
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem(RESOURCE_BUDGET_STORAGE_KEY(projectId), JSON.stringify(data));
    console.log('[MockBudgetStorage] Saved resource budget roles for project', projectId);
  } catch (error) {
    console.error('[MockBudgetStorage] Error saving resource budget:', error);
  }
}

/**
 * Load resource budget role data from localStorage (demo mode only).
 * Returns null if nothing has been saved yet.
 */
export function loadMockResourceBudgetRoles(
  projectId: number
): RoleBudget[] | null {
  try {
    const stored = localStorage.getItem(RESOURCE_BUDGET_STORAGE_KEY(projectId));
    if (!stored) return null;
    const data: StoredResourceBudget = JSON.parse(stored);
    console.log('[MockBudgetStorage] Loaded resource budget roles for project', projectId);
    return data.byRole;
  } catch (error) {
    console.error('[MockBudgetStorage] Error loading resource budget:', error);
    return null;
  }
}

/**
 * Clear resource budget role data from localStorage.
 */
export function clearMockResourceBudgetRoles(projectId: number): void {
  try {
    localStorage.removeItem(RESOURCE_BUDGET_STORAGE_KEY(projectId));
    console.log('[MockBudgetStorage] Cleared resource budget roles for project', projectId);
  } catch (error) {
    console.error('[MockBudgetStorage] Error clearing resource budget:', error);
  }
}

// ── Cost Budget mock storage (Phase 6 — Category mode) ────────────────────
// Used to persist category data entered in the Cost Budget edit screen (demo mode)
// so the tab view and refetchBudget() return the saved data rather than the original mock.

const COST_BUDGET_STORAGE_KEY = (projectId: number) =>
  `mock_cost_budget_${projectId}`;

export interface StoredCostBudgetCategories {
  projectId: number;
  categories: Array<{
    id?: number;
    name: string;
    code?: string;
    type: string;
    budget: number;
    actual?: number;
    committed?: number;
    available?: number;
    progressPercent?: number;
    scope?: string;
    globalCategoryId?: number;
    isCustom?: boolean;
    monthlyBudgets?: Record<string, number>;
  }>;
  lastUpdated: string;
}

/**
 * Save cost budget categories to localStorage (demo mode only).
 */
export function saveMockCostBudgetCategories(
  projectId: number,
  categories: StoredCostBudgetCategories['categories']
): void {
  try {
    const data: StoredCostBudgetCategories = {
      projectId,
      categories,
      lastUpdated: new Date().toISOString(),
    };
    localStorage.setItem(COST_BUDGET_STORAGE_KEY(projectId), JSON.stringify(data));
    console.log('[MockBudgetStorage] Saved cost budget categories for project', projectId);
  } catch (error) {
    console.error('[MockBudgetStorage] Error saving cost budget categories:', error);
  }
}

/**
 * Load cost budget categories from localStorage (demo mode only).
 * Returns null if nothing has been saved yet.
 */
export function loadMockCostBudgetCategories(
  projectId: number
): StoredCostBudgetCategories['categories'] | null {
  try {
    const stored = localStorage.getItem(COST_BUDGET_STORAGE_KEY(projectId));
    if (!stored) return null;
    const data: StoredCostBudgetCategories = JSON.parse(stored);
    console.log('[MockBudgetStorage] Loaded cost budget categories for project', projectId);
    return data.categories;
  } catch (error) {
    console.error('[MockBudgetStorage] Error loading cost budget categories:', error);
    return null;
  }
}

/**
 * Clear cost budget category data from localStorage.
 */
export function clearMockCostBudgetCategories(projectId: number): void {
  try {
    localStorage.removeItem(COST_BUDGET_STORAGE_KEY(projectId));
    console.log('[MockBudgetStorage] Cleared cost budget categories for project', projectId);
  } catch (error) {
    console.error('[MockBudgetStorage] Error clearing cost budget categories:', error);
  }
}
